import React, { useCallback, useRef } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { useDescText } from 'src/hooks/useDescText';
import { DESC_BTN, DESC_FLD } from 'src/utils/descLanguage/descLanguageUtil';

import { useKSK2020_R01Store } from '../Store_KSK2020_R01';
import { AgGridReact } from 'ag-grid-react';
import { KSK2020_R01HeaderDto, KSK2020_R01InspectDto } from '../Dto_KSK2020_R01';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { showWarning } from 'src/utils/toast/toastUtil';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { validateEditBadQtyAgainstInspectList } from '../util/inspectBadQtyUtil_KSK2020_R01';
import { InspectTable_KSK2020_R01 as InspectTableKSK2020R01 } from "../table/InspectTable_KSK2020_R01";

interface Props {
	defaultKSK2020_R01HeaderRow: KSK2020_R01HeaderDto;
    inspectGridRef: React.RefObject<AgGridReact<KSK2020_R01InspectDto> | null>;
	defaultKSK2020_R01InspectRow: KSK2020_R01InspectDto;
	handleInspectConfirmClick: () => Promise<void>;
	handleInspectDeleteClick: () => Promise<void>;
}

export const Modal_KSK2020_R01 = ({
	defaultKSK2020_R01HeaderRow,
    inspectGridRef,
    defaultKSK2020_R01InspectRow,
	handleInspectConfirmClick,
	handleInspectDeleteClick,
}: Props) => {
	const { renderDescText } = useDescText();
	const toolbarButtonClass = 'h-[40px] min-w-[64px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[14px] text-[15px] font-bold text-white shadow-sm hover:brightness-105';
	const deleteButtonClass = 'h-[40px] min-w-[64px] rounded-[8px] border border-[#ffbdc5] bg-gradient-to-b from-[#fff7f8] to-[#ffe8eb] px-[14px] text-[15px] font-bold text-[#d8324a] shadow-sm hover:brightness-105';
    const { 
		modalVisible, setModalVisible,
		editKSK2020_R01, setEditKSK2020_R01,
		KSK2020_R01InspectList,
		deletedKSK2020_R01InspectList,
		insertKSK2020_R01InspectList, 
		removeKSK2020_R01InspectList,
	 } = useKSK2020_R01Store();

    const modalRef = useRef<HTMLDivElement>(null);
    const modalInfoRef = useRef<HTMLDivElement>(null);
    const isResizing_modal = useRef(false);

    const RESIZER_MODAL = {
        code: 'KSK2020_R01_modal',
        type: 'width',
        storageKey: 'KSK2020_R01-modal-split-width',
        min: 5,
        max: 80,
        default: 50
    } as const;

    const {
        splitValue: splitValue_modal,
        handleMouseDown: handleMouseDown_modal,
    } = useMouseContent({
        resizer: RESIZER_MODAL,
        isResizing: isResizing_modal,
        containerRef: modalRef,
        infoRef: modalRef
    });

	const handleBadQtyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setEditKSK2020_R01({
			...defaultKSK2020_R01HeaderRow,
			...editKSK2020_R01,
			[name]: value,
			badQty: Number(value) || 0
		});
	}, [editKSK2020_R01, defaultKSK2020_R01HeaderRow, setEditKSK2020_R01]);

	const handleRemoveLastRow = useCallback(() => {
		if (KSK2020_R01InspectList.length > 1) {
			const lastItem = KSK2020_R01InspectList[KSK2020_R01InspectList.length - 1];
			removeKSK2020_R01InspectList(lastItem.valueIndex);
		}else{
			showWarning('마지막 행은 삭제할 수 없습니다.');
		}
	}, [KSK2020_R01InspectList, removeKSK2020_R01InspectList]);

	const validateInspectBadQty = useCallback(() => {
		const deletedInspectListToNumber = deletedKSK2020_R01InspectList.map(value => Number(value));
		const result = validateEditBadQtyAgainstInspectList(
			editKSK2020_R01?.badQty || 0,
			KSK2020_R01InspectList,
			deletedInspectListToNumber,
		);

		if (!result.valid) {
			showWarning(result.message!);
			return false;
		}
		return true;
	}, [editKSK2020_R01?.badQty, deletedKSK2020_R01InspectList, KSK2020_R01InspectList]);

	const handleConfirmClick = useCallback(async () => {
		if (!validateInspectBadQty()) return;
		await handleInspectConfirmClick();
	}, [handleInspectConfirmClick, validateInspectBadQty]);

	const handleCancelClick = useCallback(() => {
		if (!validateInspectBadQty()) return;
		setModalVisible(false);
	}, [validateInspectBadQty, setModalVisible]);

    if (!modalVisible) return null;

    return (
        <ModalWrapper
            size={'qmd'}
            defaultWidth={600}
            overlay={true}
            onOverlayClick={handleCancelClick}
            onEscKeyDown={handleCancelClick}
        >
			<div className="flex h-full min-h-[460px] flex-col overflow-hidden rounded-[6px] bg-[#f5f8fc]">
				<div className="flex h-[58px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[22px] text-white">
					<WarningOutlined className="text-[20px]" />
					<p className="text-[21px] font-bold">{renderDescText(DESC_FLD.DEFECT_DETAILS_REGISTRATION, "불량 내용 등록")}</p>
				</div>
				<div className="flex min-h-0 flex-1 flex-col gap-[10px] p-[16px]">
					<div className="flex shrink-0 items-center justify-between gap-[8px] rounded-[10px] border border-[#d8e3f0] bg-white p-[10px] shadow-sm">
						<div className="flex items-center gap-[8px]">
							<button type="button" className={toolbarButtonClass} onClick={() => insertKSK2020_R01InspectList([defaultKSK2020_R01InspectRow])} disabled={false}>{renderDescText(DESC_FLD.ROW_ADD, "행+")}</button>
							<button type="button" className={toolbarButtonClass} onClick={handleRemoveLastRow} disabled={false}>{renderDescText(DESC_FLD.ROW_DELETE, "행-")}</button>
							<button type="button" className={deleteButtonClass} onClick={() => void handleInspectDeleteClick()} disabled={false}>{renderDescText(DESC_BTN.DELETE, "삭제")}</button>
						</div>
						<div className="min-w-[230px] w-full max-w-[300px]">
							<InputForm title={renderDescText(DESC_FLD.DEFECT_QTY, "불량수량")} value={editKSK2020_R01?.badQty || 0} editable={true} name="badQty" onChange={handleBadQtyChange} dragSelectAll={true} wrapperOption={{
								wrapperStyle: "!h-[40px] w-full min-w-0",
								titleStyle: "flex h-[40px] w-[120px] min-w-[120px] items-center rounded-[8px] bg-[#e8eff8] px-[10px] text-left text-[15px] font-bold whitespace-nowrap text-[#183153]",
								inputStyle: "flex-1 min-w-0 [&>input]:!h-[40px] [&>input]:!rounded-[8px] [&>input]:!border-[#c5d5e9] [&>input]:!px-[10px] [&>input]:text-[16px]",
							}} />
						</div>
					</div>
					<div className="min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#d8e3f0] bg-white shadow-sm" ref={modalRef}>
						<div className="h-full w-full" ref={modalInfoRef}>
							<InspectTableKSK2020R01 gridRef={inspectGridRef} defaultKSK2020_R01InspectRow={defaultKSK2020_R01InspectRow} />
						</div>
					</div>
				</div>
				<div className="flex shrink-0 items-center justify-end gap-[8px] border-t border-[#d8e3f0] bg-white px-[18px] py-[12px]">
					<button type="button" className="h-[40px] min-w-[86px] rounded-[8px] border border-[#bdcadd] bg-white px-[16px] text-[15px] font-bold text-[#52657d] shadow-sm hover:bg-[#eef2f7]" onClick={handleCancelClick} disabled={false}>{renderDescText(DESC_BTN.CANCEL, "취소")}</button>
					<button type="button" className="h-[40px] min-w-[86px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[16px] text-[15px] font-bold text-white shadow-md hover:brightness-105" onClick={() => void handleConfirmClick()} disabled={false}>{renderDescText(DESC_BTN.CONFIRM, "확인")}</button>
				</div>
			</div>
        </ModalWrapper>
    );
};
